import React, { Component } from 'react';
import { Flex, Box } from 'reflexbox';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Form from 'components/Form';
import Helmet from 'react-helmet-async';
import CloseButton from 'components/CloseButton';
import PrintButton from './components/PrintButton';
import Button from 'components/Button';
import FormInput from 'components/Input';
import Text from 'components/Text';
import NewList from './components/NewList';

// Modules
import Validate from 'helpers/Validate';
import { getText } from 'helpers/i18n';

import './listSelector.styles.scss';

import imgArrowDown from 'images/chevron-down-thin-24.svg';
import imgDelete from 'images/remove-thin-24.svg';
import imgEdit from 'images/edit-thin-24.svg';

export default class ListSelector extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showListManager: props.showListManager || false,
        };
    }
    componentDidMount() {
        const { getShoppingLists, lists } = this.props;
        if (!lists || Object.keys(lists).length === 0) {
            getShoppingLists();
        }

        if (lists) {
            this.cacheEditableNames(lists);
        }

        Modal.setAppElement('#one-checkout');
    }

    componentDidUpdate(prevProps) {
        const { isLogged, getShoppingLists, lists } = this.props;

        if (isLogged !== null && isLogged !== prevProps.isLogged) {
            getShoppingLists();
        }

        if (lists !== prevProps.lists) {
            this.cacheEditableNames(lists);
        }
    }

    onListChange(value) {
        const { changeList, selectedList } = this.props;

        if (value.toString() !== selectedList.toString()) {
            changeList(value);
        }
        this.onToggleManager(false);
    }

    onToggleManager(value) {
        this.setState({
            showListManager: value,
        });
    }

    onDeleteListConfirm(listId, value) {
        const { toggleListDeleteConfirmation } = this.props;

        toggleListDeleteConfirmation(listId, value);
    }

    onToggleEditForm(listId, value) {
        const { toggleListEdit } = this.props;
        toggleListEdit(listId, value);
    }

    onUpdateCachedName(listId, name, value) {
        this.setState({
            editableNames: Object.assign({}, this.state.editableNames, { [listId]: value }),
        });
    }

    onSaveEdit(listId) {
        const { editShoppingListName } = this.props;
        const newName = this.editListInput.state.value;
        const listSelectorValidator = Validate(newName, this.editListInput.props.validator);

        if (listSelectorValidator.valid) {
            editShoppingListName(listId, newName);
            this.onToggleEditForm(listId, false);
        }
        return null;
    }

    render() {
        const { lists, selectedList, deleteShoppingList } = this.props;

        if (!lists || Object.keys(lists).length === 0) {
            return null;
        }
        const formattedList = [];
        if (this.state.editableNames) {
            for (const list in lists) {
                const listElement = (
                    <li key={list}>
                        {(() => {
                            if (lists[list].showEdit) {
                                return (
                                    <Form>
                                        <Flex wrap id={'editListConfirmation' + list} justify="flex-end">
                                            <Box w={1} pb={1}>
                                                <FormInput
                                                    ref={(input) => { this.editListInput = input; }}
                                                    type="text"
                                                    name={'edit_list_' + list}
                                                    id={'edit_list_' + list}
                                                    ariaLabel={getText('list_selector_edit_input_label')}
                                                    ariaRequired
                                                    onChange={this.onUpdateCachedName.bind(this, list)}
                                                    value={this.state.editableNames[list]}
                                                    validate={Validate}
                                                    validator={{
                                                        required: true,
                                                        errorMessage: {
                                                            required: 'list_selector_edit_empty',
                                                        },
                                                    }}
                                                    autoFocus
                                                />
                                            </Box>
                                            <Box className="edit_list__button-cancel" w={1 / 3}>
                                                <Button
                                                    theme="secondary"
                                                    ariaLabel={getText('list_selector_edit_cancel')}
                                                    text="list_selector_edit_cancel"
                                                    onClick={this.onToggleEditForm.bind(this, list, false)}
                                                />
                                            </Box>
                                            <Box className="edit_list__button-save" w={1 / 3}>
                                                <Button
                                                    theme="primary"
                                                    type="submit"
                                                    ariaLabel={getText('list_selector_edit_save')}
                                                    text="list_selector_edit_save"
                                                    onClick={this.onSaveEdit.bind(this, list)}
                                                />
                                            </Box>
                                        </Flex>
                                    </Form>
                                );
                            }
                            return (
                                <Flex align="center" className={`listselector__listitem ${list.toString() === selectedList.toString() ? 'listselector__listitem--active' : ''}`}>
                                    <button className="button button--link" onClick={this.onListChange.bind(this, lists[list].value)}>
                                        <span className="listselector__listitem_name">
                                            {lists[list].name}
                                        </span>
                                        {lists[list].noOfItems !== 0 &&
                                            <sup className="listselector__product-count-label">{lists[list].noOfItems}</sup>
                                        }
                                    </button>
                                    <Button
                                        theme="unstyled"
                                        className="listselector__listitem-edit"
                                        onClick={this.onToggleEditForm.bind(this, list, true)}
                                        icon={imgEdit}
                                        iconOnly
                                        text={`${getText('list_selector_edit_input_label')} ‘${lists[list].name}’`}
                                        aria-describedby={'editListConfirmation' + list}
                                    />
                                    <Button
                                        theme="unstyled"
                                        className="listselector__listitem-delete"
                                        onClick={this.onDeleteListConfirm.bind(this, list, true)}
                                        icon={imgDelete}
                                        iconOnly
                                        text={`${getText('list_selector_remove_list')} ‘${lists[list].name}’`}
                                        aria-describedby={'deleteListConfirmation' + list}
                                    />
                                </Flex>
                            );
                        })()}
                        {lists[list].showDeleteConfirmation &&
                            <Flex wrap w={1} py={2} justify="flex-end" role="alertdialog" className="product__remove-confirmation">
                                <Box w={1} pb={1} className="product__remove-confirmation-text" id={'deleteListConfirmation' + list}>
                                    <Text textKey="list_selector_remove_list_notice" />
                                </Box>
                                <Box w={1 / 3} className="product__remove-confirmation-no">
                                    <Button
                                        theme="secondary"
                                        ariaLabel={getText('list_selector_remove_list_cancel')}
                                        text="list_selector_remove_list_cancel"
                                        onClick={this.onDeleteListConfirm.bind(this, list, false)}
                                        autoFocus
                                    />
                                </Box>
                                <Box w={1 / 3} className="product__remove-confirmation-yes">
                                    <Button
                                        theme="danger"
                                        ariaLabel={getText('list_selector_remove_list_confirm')}
                                        text="list_selector_remove_list_confirm"
                                        onClick={deleteShoppingList.bind(this, list)}
                                    />
                                </Box>
                            </Flex>
                        }
                    </li>
                );

                formattedList[lists[list].sortId - 1] = listElement;
            }
            // API returns sort order as most recently modified at the top, but we want new lists to append
            formattedList.reverse();
        }

        return (
            <Box className="listselector">
                <Helmet>
                    <title>{lists && lists.hasOwnProperty(selectedList) && lists[selectedList].name}</title>
                </Helmet>
                {lists.hasOwnProperty(selectedList) &&
                    <h1 className="listselector__name">
                        <button className="button button--link" onClick={this.onToggleManager.bind(this, true)}>
                            {lists[selectedList].name} <img src={imgArrowDown} alt={getText('list_selector_open_button_label')} />
                        </button>
                        <PrintButton />
                    </h1>
                }
                <Modal
                    isOpen={this.state.showListManager}
                    onRequestClose={this.onToggleManager.bind(this, false)}
                    contentLabel={getText('list_selector_headline')}
                    className="one-checkout ikea-modal__content"
                    overlayClassName="ikea-modal__overlay"
                >
                    <CloseButton onClick={this.onToggleManager.bind(this, false)} container="modal" />
                    <h2><Text textKey="list_selector_headline" /></h2>
                    <ul className="listselector__list">
                        {formattedList}
                    </ul>
                    <NewList/>
                </Modal>
            </Box>
        );
    }

    cacheEditableNames(lists) {
        if (lists) {
            const editableNames = Object.keys(lists).reduce((acc, key) => {
                acc[key] = lists[key].name;
                return acc;
            }, {});
            this.setState({ editableNames });
        }
    }
}

ListSelector.propTypes = {
    changeList: PropTypes.func,
    getShoppingLists: PropTypes.func.isRequired,
    lists: PropTypes.objectOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        noOfItems: PropTypes.number,
        sortId: PropTypes.number,
        value: PropTypes.number,
    })),
    selectedList: PropTypes.any,
    isLogged: PropTypes.bool,
    showListManager: PropTypes.bool,
    toggleListDeleteConfirmation: PropTypes.func.isRequired,
    deleteShoppingList: PropTypes.func.isRequired,
    toggleListEdit: PropTypes.func.isRequired,
    editShoppingListName: PropTypes.func.isRequired,
};
